import React, { useEffect } from "react";
import ReactFlagsSelect from "react-flags-select";
import FormalityHeader from "../header/FormalityHeader";
import { SummaryBar } from "../options/OptionParts";
import { formatPrice } from "../../utils/formatPrice";
import "../../views/styles/options.css";
import "../../views/styles/flow-form.css";

// Display pieces shared by the single-page formalities

// Countries where these formalities are available
export const FORMALITY_COUNTRIES = ["CM"];

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

// Error message of a required field, null when it is valid
export const fieldError = (value, type) => {
  const text = String(value || "").trim();

  if (!text) return "Ce champ est obligatoire.";
  if (type === "email" && !isEmail(text)) return "Adresse e-mail invalide.";
  return null;
};

export function FormalityPage({
  from,
  title,
  lead,
  price,
  submitting,
  onContinue,
  children,
}) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="options-page flow-fields">
      <FormalityHeader from={from} />

      <div className="container options-container">
        <header className="options-header">
          <h1 className="options-title">{title}</h1>
          <p className="options-lead">{lead}</p>
        </header>

        <div className="seat-panel identity-panel">{children}</div>
      </div>

      <SummaryBar
        label={title}
        total={formatPrice(price)}
        buttonLabel={submitting ? "Enregistrement…" : "Continuer"}
        disabled={submitting}
        onContinue={onContinue}
      />
    </div>
  );
}

export const CountryField = ({ value, onChange }) => (
  <div className="flow-field">
    <span className="flow-label">Pays de la démarche</span>
    <ReactFlagsSelect
      selected={value}
      countries={FORMALITY_COUNTRIES}
      customLabels={{ CM: "Cameroun" }}
      onSelect={onChange}
      placeholder="Choisissez le pays"
      className="w-100"
    />
    <p className="flow-hint">
      Pour l'instant, ce service est disponible au Cameroun.
    </p>
  </div>
);

export const Choices = ({ label, options, value, onChange, error }) => (
  <div className="flow-field">
    <span className="flow-label">{label}</span>
    <div className="flow-choices" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          className={`flow-choice ${value === option.value ? "is-selected" : ""}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
    {error && <p className="flow-error">{error}</p>}
  </div>
);

export const TextField = ({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  error,
}) => (
  <div className="flow-field">
    <label htmlFor={id} className="flow-label">
      {label}
    </label>
    <input
      id={id}
      type={type}
      className={`flow-input ${error ? "is-invalid" : ""}`}
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
    {error && <p className="flow-error">{error}</p>}
  </div>
);

// Lets the client reuse the details of a company known on Platform98
export const CompanyPicker = ({ companies, onPick }) => {
  const named = (companies || []).filter((company) => company.businessName);

  if (named.length === 0) return null;

  return (
    <div className="flow-field">
      <label htmlFor="formality-company" className="flow-label">
        Reprendre une de mes entreprises <small>(facultatif)</small>
      </label>
      <select
        id="formality-company"
        className="flow-input"
        defaultValue=""
        onChange={(event) =>
          onPick(named.find((company) => company._id === event.target.value))
        }
      >
        <option value="">Saisir les informations moi-même</option>
        {named.map((company) => (
          <option key={company._id} value={company._id}>
            {company.businessName}
          </option>
        ))}
      </select>
    </div>
  );
};

export const DocumentsToProvide = ({ documents, hint }) => (
  <div className="flow-field">
    <span className="flow-label">Documents à fournir à l'étape suivante</span>
    <ul className="flow-list">
      {documents.map((document) => (
        <li key={document}>{document}</li>
      ))}
    </ul>
    {hint && <p className="flow-hint">{hint}</p>}
  </div>
);
